import { ActivationFunction, IOutputVectorParameters } from '../output-vector-interfaces';

export const calculateActivationFunction = (activationFunction: ActivationFunction, value: number): [number, string] => {
    switch (activationFunction) {
        case ActivationFunction.none: {
            return [
                value,
                value.toString(),
            ];
        }
        case ActivationFunction.relu: {
            return [
                Math.max(0, value),
                ` max(0, ${ value } `
            ];
        }
        case ActivationFunction.hard_limit: {
            const result = value >= 0 ? 1 : 0;

            return [
                result,
                `(${value} >= 0 ? 1 : 0) = ${result}`
            ];
        }
        case ActivationFunction.linear: {
            return [
                value,
                value.toString(),
            ];
        }
        case ActivationFunction.sigmoid: {
            return [
                1 / (1 + Math.exp(-value)),
                `1 / (1 + exp(-${ value }))`
            ];
        }
        case ActivationFunction.tanh: {
            return [
                Math.tanh(value),
                `tanh(${ value })`
            ];
        }
        case ActivationFunction.softmax: {
            console.warn('Softmax is typically applied to a vector, not a single value. Returning exp(x) instead.');
            return [
                Math.exp(value),
                `exp(${ value })`
            ];
        }
        default: {
            return [
                value,
                value.toString(),
            ];
        }
    }
};

export const calculateOutputVector = (params: IOutputVectorParameters) => {
    const calculations: string[] = [];
    const resultsMap: Map<string, number> = new Map();

    // Step 1: initialize known inputs
    for (const [name, value] of params.input) {
        resultsMap.set(name, value);
    }

    // Step 2: get all unique target nodes (to compute)
    const nodesToProcess = new Set(params.connections.map(conn => conn[1]));
    const processed = new Set<string>();

    // Step 3: loop until all nodes are processed
    while (processed.size < nodesToProcess.size) {
        let progressMade = false;

        for (const node of nodesToProcess) {
            if (processed.has(node)) continue;

            const connections = params.connections.filter(conn => conn[1] === node);
            const parts: string[] = [];

            let ready = true;
            let sum = 0;

            for (const [from, , weight] of connections) {
                const val = resultsMap.get(from);
                if (val === undefined) {
                    ready = false;
                    break;
                }
                parts.push(`${val} * ${weight}`);
                sum += val * weight;
            }

            if (!ready) continue;

            const [activated, _formula] = calculateActivationFunction(params.activationFunction, sum);
            resultsMap.set(node, activated);
            calculations.push(`${node}: ${params.activationFunction}(${parts.join(' + ')}) = ${activated}`);
            processed.add(node);
            progressMade = true;
        }

        if (!progressMade) {
            throw new Error('Circular or missing dependency detected. Cannot resolve remaining nodes.');
        }
    }

    return {
        resultsMap,
        calculations,
    };
};

export const calculateActivationFunctionVector = (
    activationFunction: ActivationFunction,
    values: number[]
): [number, string][] => {
    switch (activationFunction) {
        case ActivationFunction.softmax: {
            const exps = values.map(v => Math.exp(v));
            const sum = exps.reduce((acc, v) => acc + v, 0);
            return exps.map((v, i) => [
                v / sum,
                `exp(${values[i]}) / (${exps.map(e => `exp(${values[exps.indexOf(e)]})`).join(' + ')}) = ${v / sum}`
            ]);
        }

        // Other vector-safe activations (optional)
        case ActivationFunction.none:
        case ActivationFunction.linear:
        case ActivationFunction.relu:
        case ActivationFunction.hard_limit:
        case ActivationFunction.sigmoid:
        case ActivationFunction.tanh: {
            return values.map(v => calculateActivationFunction(activationFunction, v));
        }

        default:
            return values.map(v => [v, v.toString()]);
    }
};